import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUnmount } from "react-use";
import { List } from "immutable";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import Task from "~/records/record.task.jsx";
import { saveFormDraft, removeFormDraft, updateFormDraftTimestamp } from "~/store/actions/actions.form.jsx";
import { makeCompaniesByProjectMembersSelector } from "~/store/selectors/selector.companies.jsx";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getTaskFormDraft } from "~/store/selectors/selector.forms.jsx";
import { makeTaskLabelsByProjectSelector } from "~/store/selectors/selector.labels.jsx";
import { getProjectSubscribers, getSortedSubscribersByProjectId } from "~/store/selectors/selector.subscription";
import { getTaskSubscriberIds } from "~/store/selectors/selector.subscription.jsx";
import { getAllTaskListsByProjectId } from "~/store/selectors/selector.taskLists.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { makeTeamsByProjectMembersSelector } from "~/store/selectors/selector.teams.jsx";
import {
  canSeeTimeEstimates,
  isLoggedUserClientSelector,
  getUsersFromProjectMembersForTaskUserSelector,
} from "~/store/selectors/selector.users.jsx";
import { getTaskFormDraftKey } from "~/utils/taskFormDraftKey.jsx";
import TaskForm from "./TaskForm.jsx";

const TaskFormContainer = ({
  id,
  projectId,
  taskListId,
  hideTaskListSelector,
  assigneeId,
  onCancel,
  onSubmit,
  submitButtonLabel,
  shouldOverflow,
  dueOn,
}) => {
  const dispatch = useDispatch();

  const initialSubscriberIds = useSelector((state) => {
    return getTaskSubscriberIds(state, id);
  });

  const initialProjectSubscriberIds = useSelector((state) => {
    return getProjectSubscribers(state, projectId);
  });

  const allTaskLists = useSelector((state) => getAllTaskListsByProjectId(state, projectId));

  const getTeamsByProjectMembersSelector = useMemo(makeTeamsByProjectMembersSelector, []);
  const getCompaniesByProjectMembersSelector = useMemo(
    () => makeCompaniesByProjectMembersSelector(projectId),
    [projectId]
  );
  const getTaskLabelsByProjectSelector = useMemo(makeTaskLabelsByProjectSelector, []);
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));
  const formKey = getTaskFormDraftKey(projectId, taskListId, id);

  const taskDraft = useSelector((state) => getTaskFormDraft(state, formKey)) || {};
  const defaultHideFromClients = useSelector((state) => getRawConfigOption(state, "default_hide_from_clients"));
  const task =
    useSelector((state) => getTaskById(state, id)) || new Task({ is_hidden_from_clients: defaultHideFromClients });
  const projectMembers = useSelector((state) => getSortedSubscribersByProjectId(state, projectId));
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);

  const taskLists = useMemo(() => {
    return hideTaskListSelector
      ? undefined
      : allTaskLists.filter(
          (taskList) => (!taskList.completed_on && !taskList.trashed_on) || taskList.id === task?.task_list_id
        );
  }, [allTaskLists, hideTaskListSelector, task?.task_list_id]);

  if (!taskListId && taskLists && taskLists.size) {
    taskListId = taskLists.first().id;
  }

  if (task.task_list_id && task.task_list_id !== taskListId) {
    taskListId = task.task_list_id;
  }
  let childData = {};
  childData.users = useSelector((state) => getUsersFromProjectMembersForTaskUserSelector(state, projectId));

  // return teams, companies filtered by projectMembers
  childData.teams = useSelector((state) => getTeamsByProjectMembersSelector(state, projectId));
  childData.companies = useSelector(getCompaniesByProjectMembersSelector);
  childData.subscribers = task.id ? initialSubscriberIds : initialProjectSubscriberIds;
  childData.sortedSubscribers = projectMembers;
  childData.canSeeTimeEstimates = useSelector((state) => canSeeTimeEstimates(state, projectId));
  childData.hasDraft = taskDraft && !!taskDraft.isDraft;
  childData.name = task.name;
  childData.description = task.body;
  childData.bodyMode = task.body_mode || "paragraph";
  childData.attachments = task.attachments;
  childData.assigneeId = task.id ? task.assignee_id : assigneeId;
  childData.startOn = task.start_on;
  childData.dueOn = dueOn ? dueOn : task.due_on;
  childData.labelIds = task.labels;
  childData.estimate = `${task.estimate}`;
  childData.jobTypeId = task.job_type_id;
  childData.hiddenFromClients = task.is_hidden_from_clients;
  childData.important = task.is_important;
  childData.hideFromClients = hideFromClients;
  childData.isLoggedUserClient = isLoggedUserClient;
  childData.taskLists = taskLists;
  childData.taskListId = taskListId;

  if (taskDraft && taskDraft.isDraft) {
    childData = {
      ...childData,
      ...taskDraft,
    };
  }

  childData.labels = useSelector((state) => getTaskLabelsByProjectSelector(state, projectId, formKey));

  const debouncedOnChange = debounce((data) => {
    return dispatch(saveFormDraft(formKey, data));
  }, 600);

  const handleSubmit = useCallback(
    (attributes) => {
      debouncedOnChange.cancel();

      dispatch(removeFormDraft(formKey));

      typeof onSubmit === "function" && onSubmit(attributes);
    },
    [debouncedOnChange, dispatch, formKey, onSubmit]
  );

  const handleUnmount = useCallback(() => {
    dispatch(updateFormDraftTimestamp(formKey));
  }, [dispatch, formKey]);

  useUnmount(() => {
    handleUnmount();
  });

  const handleChange = useCallback(
    (data) => {
      debouncedOnChange(data);
    },
    [debouncedOnChange]
  );

  const handleCancel = useCallback(() => {
    dispatch(removeFormDraft(formKey));
    typeof onCancel === "function" && onCancel();
  }, [dispatch, formKey, onCancel]);

  return (
    <TaskForm
      id={id}
      projectId={projectId}
      users={childData.users}
      teams={childData.teams}
      companies={childData.companies}
      attachments={childData.attachments}
      taskLists={taskLists}
      submitButtonLabel={submitButtonLabel}
      canSeeTimeEstimates={childData.canSeeTimeEstimates}
      name={childData.name}
      description={childData.description}
      bodyMode={childData.bodyMode}
      taskListId={taskListId}
      assigneeId={childData.assigneeId}
      startOn={childData.startOn}
      dueOn={childData.dueOn}
      hiddenFromClients={childData.hiddenFromClients}
      important={childData.important}
      jobTypeId={childData.jobTypeId}
      estimate={childData.estimate}
      subscribers={List.isList(childData.subscribers) ? childData.subscribers.toArray() : childData.subscribers}
      projectMembers={childData.sortedSubscribers}
      attachUploadedFiles={childData.attachUploadedFiles}
      dropAttachedFiles={childData.dropAttachedFiles}
      labels={childData.labels}
      labelNames={childData.labelNames}
      labelIds={childData.labelIds}
      onCancel={handleCancel}
      onChange={handleChange}
      hasDraft={childData.hasDraft}
      onSubmit={handleSubmit}
      hideFromClients={hideFromClients}
      isLoggedUserClient={isLoggedUserClient}
      shouldOverflow={shouldOverflow}
    />
  );
};

TaskFormContainer.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectId: PropTypes.number.isRequired,
  taskListId: PropTypes.number,
  assigneeId: PropTypes.number,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  submitButtonLabel: PropTypes.string,
  hideTaskListSelector: PropTypes.bool,
  dueOn: PropTypes.number,
  shouldOverflow: PropTypes.bool,
};

TaskFormContainer.defaultchildData = {
  id: 0,
  hasDraft: false,
  hideTaskListSelector: false,
};

TaskFormContainer.displayName = "TaskFormContainer";

export default TaskFormContainer;
